import {
  Badge,
  Button,
  ButtonProps,
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  HStack,
  Avatar,
  Wrap,
  Separator,
} from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { CommentThread } from '@src/components/sco/Comments/CommentThread'
import { AttendEventButton } from '@src/components/sco/Events/AttendEventButton'
import { OfficeHoursScheduler } from '@src/components/sco/Events/OfficeHoursScheduler'
import { getEventJoinWindow, getJoinEventPath } from '@src/components/sco/Events/eventJoinWindow'
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { GroupEvent } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import moment from 'moment'
import React from 'react'
import { GoMention } from 'react-icons/go'

type EventProfileProps = {
  event: GroupEvent
  hideComments?: boolean
}

export const CategoryButton = (props: { bgColor: ButtonProps['bgColor']; title: string }) => (
  <Badge
    bgColor={props.bgColor || 'primary'}
    borderRadius={'2px'}
    boxShadow={'base'}
    color={'white'}
    flexGrow={1}
    fontFamily={'Poppins, sans-serif'}
    fontSize={'10px'}
    fontWeight={'600'}
    letterSpacing={0}
    lineHeight={0.5}
    marginRight={'1'}
    padding={2}
    textTransform={'none'}
  >
    {props.title || 'Event'}
  </Badge>
)

export const EventProfile = (props: EventProfileProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const { event } = props

  if (!event) {
    return <></>
  }

  const { canBeJoined } = getEventJoinWindow(event.startsAt, event.endsAt)
  const joinPath = getJoinEventPath(event)

  return (
    <Box width={'full'}>
      <VStack gap={8} width={'full'}>
        {/* Event Details */}
        <VStack
          alignItems={'flex-start'}
          bgColor={'colorPalette.container'}
          borderRadius={['none', '2xl']}
          boxShadow={'sm'}
          gap={0}
          width={'full'}
        >
          <VStack
            align={'stretch'}
            bg={`linear-gradient(to bottom, ${event.type?.colorHexCode}80, ${event.type?.colorHexCode}50 100%)`}
            borderTopRadius={['none', '2xl']}
            direction={'column'}
            gap={6}
            justify={'left'}
            padding={getBreakpointValue([4, 8, 10, 12])}
            textAlign={'left'}
            width={'full'}
          >
            <Box width={'100%'}>
              {event.type && <CategoryButton bgColor={event.type.colorHexCode} title={event.type.title} />}

              {event.topics && (
                <>
                  {event.topics
                    .filter(t => t.isPrimary && t.title)
                    .map(topic => (
                      <CategoryButton key={topic.title} bgColor={topic.colorHexCode} title={topic.title} />
                    ))}
                  {event.topics
                    .filter(t => !t.isPrimary && t.title)
                    .map(topic => (
                      <CategoryButton key={topic.title} bgColor={topic.colorHexCode} title={topic.title} />
                    ))}
                </>
              )}
            </Box>
            <VStack align={'flex-start'} gap={4}>
              <Heading size={getBreakpointValue(['lg', 'xl', '2xl', '2xl'])}>{event.title}</Heading>
              {event.typeKey === 'office-hours' ? (
                <Box>
                  <OfficeHoursScheduler
                    event={event}
                    buttonProps={{
                      bgColor: 'actionActive',
                      color: 'black',
                      paddingX: '2em',
                      width: 'auto',
                    }}
                  />
                </Box>
              ) : (
                <>
                  <Text color={'colorPalette.fg'} fontWeight={'semibold'} textStyle={'md'}>
                    {moment(event.startsAt).format('dddd MMM Do, YYYY')}
                    <Icon asChild color={'colorPalette.fg'} marginX={'2'}>
                      <GoMention />
                    </Icon>
                    {moment(event.startsAt).format('h:mm a')}{' '}
                    {
                      new Date()
                        .toLocaleTimeString('en-us', {
                          timeZoneName: 'short',
                        })
                        .split(' ')[2]
                    }
                  </Text>
                  <Flex paddingTop={4}>
                    <PaywalledAction
                      isUnlocked={event.isFree}
                      tokens={event.title ? { eventName: event.title } : undefined}
                      upgradeCtaLabel={'Attend'}
                      contentPermission={
                        event.isPremium ? 'canAccessFGPremiumEvents' : 'canAccessFGBasicEvents'
                      }
                    >
                      {canBeJoined && joinPath ? (
                        <Button
                          asChild
                          bgColor={'actionActive'}
                          color={'black'}
                          paddingX={'2em'}
                          width={'auto'}
                        >
                          <a href={joinPath} rel={'noreferrer'} target={'_blank'}>
                            Join Event Now
                          </a>
                        </Button>
                      ) : (
                        <AttendEventButton
                          event={event}
                          buttonProps={{
                            bgColor: 'actionActive',
                            color: 'black',
                            paddingX: '2em',
                            width: 'auto',
                          }}
                        />
                      )}
                    </PaywalledAction>
                  </Flex>
                </>
              )}
            </VStack>
          </VStack>
          <VStack align={'stretch'} gap={0} paddingY={getBreakpointValue([4, 8, 10, 12])}>
            <VStack align={'flex-start'} gap={4} paddingX={getBreakpointValue([4, 8, 10, 12])}>
              <Text color={'colorPalette.fg/50'} textStyle={'label'}>
                Description
              </Text>
              <Box>
                <MarkdownRenderer preset={'article'}>{event.description}</MarkdownRenderer>
              </Box>
            </VStack>
            {event.hosts?.length > 0 && (
              <VStack
                align={'stretch'}
                gap={4}
                paddingBottom={4}
                paddingX={getBreakpointValue([4, 8, 10, 12])}
              >
                <Separator />
                <Text color={'colorPalette.fg/50'} textStyle={'label'}>
                  Hosted By
                </Text>
                <Wrap gap={6}>
                  {event.hosts.map(host => (
                    <HStack key={host.id} align={'center'} justify={'center'}>
                      <Avatar.Root>
                        <Avatar.Fallback name={[host.firstName, host.lastName].filter(Boolean).join(' ')} />
                        <Avatar.Image src={host.pictureUrl || null} />
                      </Avatar.Root>
                      <Box width={'full'}>
                        <Text textStyle={'labelSm'}>
                          {host.firstName} {host.lastName}
                        </Text>
                        <Text fontSize={'12px'}>{host.tagline}</Text>
                      </Box>
                    </HStack>
                  ))}
                </Wrap>
              </VStack>
            )}
            {event.typeKey !== 'office-hours' && event.attendingUsers?.items?.length > 0 && (
              <VStack align={'flex-start'} gap={2} paddingX={getBreakpointValue([4, 8, 10, 12])} paddingY={2}>
                <Text opacity={0.4} textStyle={'badgeSm'}>
                  {event.attendingUsers?.items?.length > 0 ? event.attendingUsers?.items?.length : ''}{' '}
                  Attendees
                </Text>
                <Wrap paddingTop={2}>
                  {event.attendingUsers.items.map(user => (
                    <Box key={user.userId} position={'relative'}>
                      <UserAvatar avatarProps={{ size: 'sm' }} user={user} userId={user.userId} />
                    </Box>
                  ))}
                </Wrap>
              </VStack>
            )}
          </VStack>
        </VStack>
        {/* Comments */}
        {!props.hideComments && (
          <Box paddingX={getBreakpointValue([0, 0, '1em', '2em'])} width={'full'}>
            <CommentThread
              attachmentId={event.groupEventId}
              attachmentType={'GroupEvent'}
              showReplies={true}
              showTopComposer={true}
            />
          </Box>
        )}
      </VStack>
    </Box>
  )
}

EventProfile.fragments = {
  GroupEvent: () => gql`
    fragment _ on GroupEvent {
      id
      groupEventId
      title
      description
      typeKey
      topicKeys
      startsAt
      endsAt
      callUrl
      eventAirtableRecordIdentifier
      schedulingUrl
      isFutureEvent
      isPremium
      isFree
      isCurrentUserAttending
      maximumAttendeeLimit
      maximumAttendeesRemaining
      attendanceCount
      featuredImageUrl
      topicKeys
      topics {
        id
        colorHexCode
        description
        isPrimary
        title
      }
      attendingUsers {
        id
        items {
          id
          fullName
          pictureUrl
          shortBio
          title
          userId
          slug
          company {
            id
            name
          }
        }
      }
      hosts {
        id
        firstName
        lastName
        pictureUrl
        tagline
      }
      typeKey
      type {
        id
        title
        description
        colorHexCode
      }
    }
  `,
}
